import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Future 2030 Logistics outlook page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **The ninth Future 2030 outlook with a designed page of its own**, and the
 * eighteenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing, Consumer Goods, Retail, Pharmaceutical, Banking,
 * Services and Insurance ones — *"referans alarak ilgili sayfayı birebir tıpa tıp
 * aynısını tasarla"* — as a four-part full-page capture of digitopia.co's Logistics
 * outlook page, plus the six images that page carries: the truck-on-a-circuit-board
 * cover art, the DMI forecast chart, the three imperative photographs and the
 * two-copy report mockup. And, as on the six requests before it, the instruction
 * that the maturity band point at the 2026 DAIMI report rather than the 2024 one the
 * live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the forecast
 * beside Figure 1 → a dark band of three imperatives → the five predictions → the
 * pointer at the maturity report → the close. The two shared closing bands
 * (`content/reportClosing.ts`) still sit where they sit on every other report page.
 *
 * **Three things here are this page's own, and all three are the reference's doing.**
 *
 *  * **The hero is a bold claim over two paragraphs, not one.** Every sibling hero is
 *    a `lede` and a single block; this one sets "Logistics Is Being Rewritten—By AI,
 *    Automation, and Expectation" over *two* — what delivery is now about, and what
 *    the decade asks of it. So `body` is an array here rather than a string.
 *  * **The imperative cards are prose again**, one sentence each — the shape the seven
 *    pages before Insurance have. Insurance's `points`/`outcome` fields are absent.
 *  * **The predictions band has a two-line lead-in.** Insurance's went from the
 *    heading straight into the five; this one sets the sector's state of play above
 *    them, the way the Retail and Banking bands do.
 *
 * **The three trajectories were re-cast, and that is the one wording liberty here.**
 * The live band runs them as three sentences — "**Leaders** are building AI-powered
 * supply chains…" — with the subject bolded inside the prose. Every designed report
 * page on this site sets that label on its own line instead, which leaves the rest of
 * the sentence needing to start like one; so "are building" becomes "Building". The
 * claim is unchanged and `OUTLOOK_LG_FIGURE.check` says so.
 *
 * **No sector slip in this reference.** The Pharmaceutical and Banking captures both
 * carry a maturity CTA reading "Explore the 2024 **Retail** Digital Maturity Report"
 * under a paragraph about their own sector. This page's CTA says Logistics, so there
 * was nothing to correct.
 *
 * **Two deliberate departures from the capture**, both the settled house call rather
 * than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Logistics") rather than the live page's "Future 2030:
 *    Logistics Industry Outlook" — the eyebrow above it already says Future 2030, and
 *    the nine outlook pages read as one series this way.
 *  * the maturity band carries the 2026 Logistics Industry Focus cover, which the live
 *    band does not. `OUTLOOK_LG_MATURITY.check` is where that is asked about, because
 *    it is the one place this page shows something the reference has not.
 *
 * **Where the words come from.** Everything here is transcribed from the four
 * captures, which are full-page renders read down to fit — the *shape* of the page is
 * certain and the *wording* is a careful reading of body copy set a handful of pixels
 * tall. Every band that carries prose therefore carries a `check`, the same way
 * `content/outlookInsurance.ts` does, and `outlookLogisticsChecks()` feeds them into
 * the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form — first and last name, business email,
 * company, job title, phone, country, industry, a marketing opt-in and the privacy
 * statement. These documents are prerendered with no client bundle at all
 * (`src/prerender.tsx`), so a form script would never run, and the other thirty
 * reports on this site hand the reader the PDF from our own bucket instead. Every
 * Download button here is that PDF, and `OUTLOOK_LG_CLOSE.check` is what keeps the
 * gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The short slug digitopia.co uses, and the one the request named. It was already in
 * `REPORTS` before this page existed, so the page is built under it as it stands
 * rather than moved to a `…-outlook-2030` one.
 */
export const OUTLOOK_LG_SLUG = 'logistics-industry-outlook'

/**
 * The Logistics Industry Focus cut of the Digital & AI Maturity Report 2026 — where
 * this page's maturity band sends the reader.
 *
 * Written out rather than imported: that report is one of the twenty drawn by the
 * shared template in `pages/Reports.tsx`, so it has no content module of its own to
 * export a slug from. It is the literal passed to `focus()` in `content/reports.ts`,
 * and the two have to stay in step.
 */
export const DAIMI_LG_SLUG = 'digital-and-ai-maturity-report-2026-logistics-industry'

/**
 * The hero.
 *
 * `lede` is set bold above the paragraphs because that is how the live hero sets it —
 * one claim in bold, the reason for it underneath — and the button says "Download the
 * Report" rather than the "Download Report" every other band uses, which is also the
 * live page's wording.
 *
 * `body` is two paragraphs rather than one: the live hero breaks between what delivery
 * is now about and what 2030 asks of the industry, and running them together would
 * lose that beat.
 */
export const OUTLOOK_LG_INTRO = {
  lede: 'Logistics Is Being Rewritten by AI, Automation, and Expectation',
  body: [
    'Global delivery is no longer just about moving goods. It’s about precision, speed, transparency, and sustainability, all in real time.',
    'As we approach 2030, the logistics industry must evolve from manual and fragmented to intelligent, integrated, and ecosystem-driven.',
  ],
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the delivery truck standing on a
   * glowing circuit board, which is the artwork behind the report cover with none of
   * its lettering on it. This is where the live hero puts it too, at the same size and
   * on the same side.
   */
  photo: {
    src: '/photo/future-2030-logistics-digital-transformation.webp',
    alt: 'A blue delivery truck standing on a glowing circuit board',
  } satisfies Photo,
  check:
    'The hero’s three lines are transcribed from the reference capture rather than supplied as text. Read them back against digitopia.co before production — the bold claim has no full stop on the live page and has none here. The image, its side and its size are the live page’s.',
}

/**
 * The forecast band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average performers", "Laggards". Split into `label` and `body`
 * here so the label can be set rather than emphasised inside a sentence, which is the
 * call every designed report page on this site has made. Their openings are re-cast as
 * a result: "Leaders **are building** AI-powered supply chains" becomes a line that can
 * start on its own.
 *
 * No `outro`: the live band goes from the laggards straight to the button, unlike the
 * Retail and Insurance ones.
 */
export const OUTLOOK_LG_FIGURE = {
  heading: 'Logistics Digital Maturity Forecast to 2030',
  intro: 'Digitopia’s five-year forecast reveals diverging futures:',
  points: [
    {
      label: 'Leaders',
      body: 'Building AI-powered supply chains with full visibility and predictive agility, on track to set the industry standard.',
    },
    {
      label: 'Average performers',
      body: 'Improving steadily, but at risk of plateauing without strategic acceleration.',
    },
    {
      label: 'Laggards',
      body: 'Facing serious disruption unless they reinvent fast, because stagnation could mean obsolescence.',
    },
  ],
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24) and
   * five of forecast (2025–29), the leader, average and laggard trajectories each
   * fanning out into an optimistic, moderate and pessimistic band. It is 1200×784 on a
   * white ground, so it gets a white card of its own and scales to it whole — ten year
   * columns with three labelled values each do get small on a phone, but a figure the
   * reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-logistics-dmi-forecast.webp',
    alt: 'Logistics DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Logistics Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Unlike the eight outlook bands before it, this one quotes no figures at all — the live page describes the three trajectories in words and leaves the numbers to Figure 1, so there is nothing here to reconcile against the chart, only wording. Two things to read back. The three lines were re-cast so each could stand on its own: the live page runs "Leaders are building AI-powered supply chains…" as prose with the label bolded inside it, and this page sets the label as a heading and opens the line with "Building…". The claim is unchanged; say if the running-prose form is wanted instead. And the same naming question the Banking, Services and Insurance pages raised: this page calls the index DMI while every other page on this site calls it DAIMI, the Digital & AI Maturity Index. The chart itself is lettered DMI, so both were left as they are.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * Prose cards, one sentence each — the shape the seven pages before Insurance have,
 * and the reference's own. The live band's second line is a title rather than a claim
 * ("Three Strategic Imperatives for 2030-Ready Logistics"), so it is set lighter and
 * larger under the headline, which is what the component already does with it.
 */
export const OUTLOOK_LG_REQUIREMENTS = {
  heading: 'Logistics Digital Revolution Has Arrived',
  subheading: 'Three Strategic Imperatives for 2030-Ready Logistics',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Achieve Operational Excellence',
      body: 'Ensure end-to-end visibility and real-time responsiveness with IoT, blockchain, and digital twins.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/logistics-operational-excellence.webp',
        alt: 'A container terminal at dusk, a gantry crane over stacked containers and a truck pulling away',
      } satisfies Photo,
    },
    {
      title: 'Go AI-First',
      body: 'Make data the backbone of decision-making: optimize routes, automate planning, forecast demand.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/logistics-ai-first.webp',
        alt: 'A white articulated lorry crossing a high viaduct through forested hills at sunrise',
      } satisfies Photo,
    },
    {
      title: 'Equip Your Workforce',
      body: 'Build digital fluency across your teams to manage AI-powered tools and adapt to real-time data.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/logistics-upskilled-workforce.webp',
        alt: 'A warehouse worker in a hard hat walking a racking aisle, reading a tablet',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card sentences are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the eight earlier outlook pages make, and one answer settles all nine.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * **A two-line lead-in**, unlike the Insurance band and like the Retail and Banking
 * ones: the live band sets the decade's stakes above the list rather than opening on
 * it. The five themselves are the clipped phrases the live page prints — no full
 * stops, no elaboration — so they are left that way.
 */
export const OUTLOOK_LG_PREDICTIONS = {
  heading: 'Five Predictions for the 2030 Logistics Industry',
  intro: [
    'Logistics is entering a decade of disruption, and of opportunity.',
    'By 2030, emerging technologies and shifting expectations will redefine how goods move, how decisions are made, and what customers demand. These five predictions reveal the most critical shifts logistics leaders must prepare for today.',
  ],
  items: [
    'Autonomous vehicles and drones will be mainstream',
    'Robots will dominate warehouse and sorting operations',
    'Micro-fulfillment will redefine last-mile delivery',
    'Digital freight marketplaces will reshape logistics planning',
    'Sustainability will become a core competitive edge',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-logistics-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Logistics',
  } satisfies Photo,
  check:
    'The heading, the two lead-in lines and all five predictions are transcribed from the reference capture. None of the five carries a figure, so there is nothing here to reconcile against Figure 1 — only wording. The mockup beside them is the file you supplied; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out. This is the same call the Retail, Banking, Pharmaceutical, Services and Insurance pages made.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a picture.**
 * digitopia.co sends the reader to a *2024* report; the instruction with the captures
 * was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a
 * yönlendirelim"*. So the CTA, the cover and the sentence all name the Logistics
 * Industry Focus cut of the Digital & AI Maturity Report 2026, which is the report
 * this site actually publishes.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left open
 * on the Automotive page and has now been answered nine times.
 */
export const OUTLOOK_LG_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Logistics Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Are you leading the shift or reacting to it? Use our data to compare your performance, assess your risks, and find the fastest route to transformation.',
  ctaLabel: 'Explore the Logistics Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_LG_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-logistics-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Logistics Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Logistics Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Three things left to decide. The rest of the paragraph is the live one, so it wants a reading. The heading is the one every outlook page here uses; the live one runs two headings together — “How Does Your Digital Maturity Compare to Industry Leaders? Benchmark Your Digital Maturity” — which reads as a template left half-filled, so say if the longer form is really wanted. And the cover beside it is not on the live page at all: it is here because the other eight Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Its heading and its sentence are the live band's own — "Download the Full Report",
 * the same close the Pharmaceutical, Banking and Services pages have rather than the
 * Retail and Insurance pages' invitation.
 */
export const OUTLOOK_LG_CLOSE = {
  heading: 'Download the Full Report',
  body: 'Get executive-level insights, 5-year projections, and actionable guidance for navigating the next phase of logistics transformation.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `outlookInsuranceChecks()` uses, and appended to the same
 * list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page arrived
 * with all six of its images. What is left is transcription nobody has read against
 * the original, one destination this site cannot offer, one image this page shows that
 * the reference does not, and the gating decision.
 */
export function outlookLogisticsChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_LG_INTRO.check },
    { section: 'outlook', check: OUTLOOK_LG_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_LG_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_LG_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_LG_MATURITY.check },
    { section: 'download', check: OUTLOOK_LG_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_LG_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
